import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { ROUTES } from '@shared/constants/routes';
import { Role } from '../types/auth.types';
import { hasPermission, type Permission } from '../config/rbac.config';

interface UseAuthGuardOptions {
  requiredRoles?: Role[];
  requiredPermissions?: Permission[];
  requireVerified?: boolean;
  redirectTo?: string;
}

export function useAuthGuard(options: UseAuthGuardOptions = {}) {
  const {
    requiredRoles,
    requiredPermissions,
    requireVerified = false,
    redirectTo = ROUTES.LOGIN,
  } = options;

  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      const returnUrl = location.pathname + location.search;
      navigate(`${redirectTo}?returnUrl=${encodeURIComponent(returnUrl)}`, {
        replace: true,
      });
      return;
    }

    if (!user) return;

    // Check email verification requirement
    if (requireVerified && !user.isVerified) {
      navigate(ROUTES.RESEND_VERIFICATION, { replace: true });
      return;
    }

    // Check role requirements
    if (requiredRoles && requiredRoles.length > 0) {
      if (!requiredRoles.includes(user.role)) {
        navigate(ROUTES.HOME, { replace: true });
        return;
      }
    }

    // Check permission requirements
    if (requiredPermissions && requiredPermissions.length > 0) {
      const hasRequiredPermissions = requiredPermissions.every((permission) =>
        hasPermission(user.role, permission)
      );
      if (!hasRequiredPermissions) {
        navigate(ROUTES.HOME, { replace: true });
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    user,
    navigate,
    location,
    redirectTo,
    requiredRoles,
    requiredPermissions,
    requireVerified,
  ]);

  return {
    isAuthorized: isAuthenticated && user !== null,
    isLoading,
    user,
  };
}
